import type { ClashChoice, ImportPreview, ItemChoice } from "./bpaImport";
import { firmKey } from "./bpaImport";

export type SavedImportChoices = {
  unticked: string[];
  choices: Record<string, ClashChoice>;
  itemChoices: Record<string, Record<string, ItemChoice>>;
  clicked: Record<string, string[]>;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function storageKey(targetEditionBpaId: string, sourceEditionBpaId: string): string {
  return `staff-ui:bpa-import:${targetEditionBpaId}:${sourceEditionBpaId}`;
}

function resolveStorage(storage?: StorageLike): StorageLike | null {
  try {
    return storage ?? window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadImportChoices(
  targetEditionBpaId: string,
  sourceEditionBpaId: string,
  storage?: StorageLike,
): SavedImportChoices | null {
  try {
    const raw = resolveStorage(storage)?.getItem(
      storageKey(targetEditionBpaId, sourceEditionBpaId),
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      unticked: Array.isArray(parsed.unticked) ? parsed.unticked : [],
      choices: parsed.choices ?? {},
      itemChoices: parsed.itemChoices ?? {},
      clicked: parsed.clicked ?? {},
    };
  } catch {
    return null;
  }
}

export function saveImportChoices(
  targetEditionBpaId: string,
  sourceEditionBpaId: string,
  saved: SavedImportChoices,
  storage?: StorageLike,
): void {
  try {
    resolveStorage(storage)?.setItem(
      storageKey(targetEditionBpaId, sourceEditionBpaId),
      JSON.stringify(saved),
    );
  } catch {
    return;
  }
}

export function clearImportChoices(
  targetEditionBpaId: string,
  sourceEditionBpaId: string,
  storage?: StorageLike,
): void {
  try {
    resolveStorage(storage)?.removeItem(storageKey(targetEditionBpaId, sourceEditionBpaId));
  } catch {
    return;
  }
}

function keepKeys<T>(record: Record<string, T>, allowed: Set<string>): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([key]) => allowed.has(key)));
}

export function applicableChoices(
  saved: SavedImportChoices,
  preview: ImportPreview,
): SavedImportChoices {
  const present = new Set(preview.orgs.map(firmKey));
  const clashing = new Set(preview.orgs.filter(org => org.clash).map(firmKey));
  return {
    unticked: saved.unticked.filter(key => present.has(key)),
    choices: keepKeys(saved.choices, clashing),
    itemChoices: saved.itemChoices,
    clicked: saved.clicked,
  };
}
